import { local } from "./storage";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError } from "./api";
import type { User } from "./types";

type Session = { user: User; expires_at: number };
type AuthContextValue = {
  user: User | null;
  loading: boolean;
  sessionError: string | null;
  retrySession: () => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};
const AuthContext = createContext<AuthContextValue | null>(null);
const clearLegacyStorage = () => {
  local.remove("nl-aos-token");
  local.remove("nl-aos-user");
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const refreshPromise = useRef<Promise<Session> | null>(null);
  const signingOut = useRef(false);
  const accept = (session: Session) => {
    setUser(session.user);
    setExpiresAt(session.expires_at);
    clearLegacyStorage();
  };
  const forget = () => {
    setUser(null);
    setExpiresAt(0);
    setSessionError(null);
    clearLegacyStorage();
  };
  const retrySession = () => {
    setSessionError(null);
    setLoading(true);
    setAttempt((n) => n + 1);
  };
  const logout = async () => {
    signingOut.current = true;
    try {
      await refreshPromise.current?.catch(() => {});
      await api("/api/auth/logout", { method: "POST" });
      forget();
    } catch {
      setSessionError(
        "Sign-out could not be confirmed. Check your connection and retry.",
      );
    } finally {
      signingOut.current = false;
    }
  };

  useEffect(() => {
    let disposed = false;
    const verify = async () => {
      try {
        // One-time exchange preserves an existing sign-in, then removes the
        // browser-readable bearer credential only after the cookie was accepted.
        const legacy = local.get("nl-aos-token");
        if (legacy)
          await api("/api/auth/session", {
            method: "POST",
            headers: { Authorization: `Bearer ${legacy}` },
          });
        const result = await api<Session>("/api/auth/me");
        if (!disposed) accept(result);
      } catch (error) {
        if (!disposed) {
          if (error instanceof ApiError && error.status === 401) forget();
          else
            setSessionError(
              "Your session could not be checked. Please retry in a moment.",
            );
        }
      } finally {
        if (!disposed) setLoading(false);
      }
    };
    void verify();
    window.addEventListener("nl-aos-session-expired", forget);
    return () => {
      disposed = true;
      window.removeEventListener("nl-aos-session-expired", forget);
    };
  }, [attempt]);

  useEffect(() => {
    let refreshing = false;
    const refresh = async () => {
      if (
        !user ||
        signingOut.current ||
        refreshing ||
        document.visibilityState !== "visible" ||
        expiresAt - Date.now() > 15 * 60_000
      )
        return;
      refreshing = true;
      try {
        refreshPromise.current = api<Session>("/api/auth/refresh", {
          method: "POST",
        });
        const session = await refreshPromise.current;
        if (!signingOut.current) accept(session);
      } catch {
        /* The API handles expiry; a transient outage is retried next minute. */
      } finally {
        refreshing = false;
        refreshPromise.current = null;
      }
    };
    const timer = window.setInterval(() => void refresh(), 60_000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [user, expiresAt]);

  const login = async (email: string, password: string) => {
    await api("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, browser_session: true }),
    });
    accept(await api<Session>("/api/auth/me"));
    setSessionError(null);
  };
  const value = useMemo(
    () => ({ user, loading, sessionError, retrySession, login, logout }),
    [user, loading, sessionError],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
/** The signed-in person when there is a session provider, or null (a shared portal link, a test). */
export function useOptionalUser() {
  return useContext(AuthContext)?.user ?? null;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
